import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  getServerDown,
  setServerDown,
  subscribeServerStatus,
  isServerDownError,
} from "Utils/serverStatus";

const HEALTH_URL = `${process.env.REACT_APP_API}/health`;
const POLL_INTERVAL_MS = 10000;

// Standalone axios instance (no global interceptors) so health pings
// never trigger error toasts or feedback loops.
const pingClient = axios.create({ timeout: 8000 });

function ServerDownOverlay() {
  const [isDown, setIsDown] = useState(getServerDown());
  const [checking, setChecking] = useState(false);
  const timerRef = useRef(null);

  const checkHealth = useCallback(async () => {
    setChecking(true);
    try {
      await pingClient.get(HEALTH_URL);
      setServerDown(false);
    } catch (e) {
      // Only treat genuine "server unreachable" errors as down.
      // Any HTTP response (e.g. 404 if /health isn't deployed) means the
      // server is reachable, so we should NOT show the maintenance screen.
      setServerDown(isServerDownError(e));
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeServerStatus(setIsDown);
    // Proactive check on startup so the screen shows even before other requests run.
    checkHealth();
    return unsubscribe;
  }, [checkHealth]);

  // Poll only while the server is considered down, to auto-recover.
  useEffect(() => {
    if (isDown) {
      timerRef.current = setInterval(checkHealth, POLL_INTERVAL_MS);
      return () => clearInterval(timerRef.current);
    }
    return undefined;
  }, [isDown, checkHealth]);

  if (!isDown) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <span role="img" aria-label="maintenance" style={styles.icon}>
            🛠️
          </span>
        </div>
        <h1 style={styles.title}>We&apos;ll be back soon</h1>
        <p style={styles.message}>
          Our server is temporarily unavailable, possibly due to maintenance or a
          connection issue. Please hold on — this page will reconnect automatically
          once the server is back.
        </p>
        <button
          type="button"
          style={{ ...styles.button, ...(checking ? styles.buttonDisabled : {}) }}
          onClick={checkHealth}
          disabled={checking}
        >
          {checking ? "Checking…" : "Try again"}
        </button>
        <p style={styles.hint}>EasySolutions Admin</p>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 999999,
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backdropFilter: "blur(4px)",
  },
  card: {
    width: "100%",
    maxWidth: "440px",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "40px 32px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  iconWrap: {
    width: "84px",
    height: "84px",
    borderRadius: "50%",
    backgroundColor: "#eef2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  icon: { fontSize: "40px" },
  title: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#0f172a",
    margin: "0 0 12px",
  },
  message: {
    fontSize: "15px",
    lineHeight: 1.6,
    color: "#475569",
    margin: "0 0 28px",
  },
  button: {
    backgroundColor: "#4f46e5",
    color: "#ffffff",
    border: "none",
    borderRadius: "12px",
    padding: "14px 28px",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
  },
  buttonDisabled: { opacity: 0.7, cursor: "not-allowed" },
  hint: {
    marginTop: "20px",
    marginBottom: 0,
    fontSize: "12px",
    color: "#94a3b8",
    letterSpacing: "0.5px",
  },
};

export default ServerDownOverlay;
